import type { FlagData } from "@/config";

/** How the debug menu edits a flag: a switch, a number input, or not at all. */
export type DebugFlagKind = "boolean" | "number" | "readonly";

/** One debug menu row: a flag's current value, its LaunchDarkly value, and whether the menu overrides it. */
export type DebugFlagRow = {
  key: string;
  kind: DebugFlagKind;
  value: unknown;
  /** `undefined` when LaunchDarkly does not serve the key. */
  launchDarklyValue: unknown;
  overridden: boolean;
};

/**
 * Builds the debug menu's rows, sorted by key and narrowed to keys containing `filter`.
 *
 * @param flags - Current flag values, overrides included.
 * @param launchDarklyFlags - Values as LaunchDarkly serves them.
 * @param overriddenKeys - Keys the debug menu has overridden.
 * @param filter - Case-insensitive substring a key must contain; empty keeps every row.
 * @returns One row per matching flag.
 */
export function debugFlagRows(
  flags: FlagData,
  launchDarklyFlags: FlagData,
  overriddenKeys: ReadonlySet<string>,
  filter: string,
): DebugFlagRow[] {
  const needle = filter.trim().toLowerCase();
  return Object.keys(flags)
    .filter((key) => key.toLowerCase().includes(needle))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    .map((key) => ({
      key,
      kind: kindOf(flags[key]),
      value: flags[key],
      launchDarklyValue: launchDarklyFlags[key],
      overridden: overriddenKeys.has(key),
    }));
}

function kindOf(value: unknown): DebugFlagKind {
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number" && Number.isFinite(value)) return "number";
  return "readonly";
}
